import {
    Card, Col, Collapse, ConfigProvider, Descriptions, Image, Row, theme as AntdTheme, theme, Typography,
} from 'antd';
import React, { useEffect, useMemo } from 'react';

import { DARK_THEME_HTML_BACKGROUND_COLOR } from '@/constants';
import { useUIStore } from '@/stores';
import { lightTheme } from '@/themes';
import {
    Script, ScriptMedia,
} from '@/types';

import { ImagePlaceholder } from './ImagePlaceholder';

type ScriptMainInfoProps = {
    script: Script | undefined;
    loading?: boolean;
    previewVisible?: boolean;
    previewIndex?: number;
    onPreviewOpen?: () => void;
    onPreviewChange?: (current: number) => void;
    onPreviewVisibleChange?: (visible: boolean) => void;
};

const getMainAndGalleryImages = (script: Script | undefined): ScriptMedia[] => {
    if (!script) return [];
    const main = script.main_image ? [script.main_image] : [];
    const gallery = script.gallery ?? [];
    return [...main, ...gallery];
};

export const ScriptMainInfo = ({
    script,
    loading,
    previewVisible = false,
    previewIndex = 0,
    onPreviewOpen,
    onPreviewChange,
    onPreviewVisibleChange,
}: ScriptMainInfoProps) => {
    const { token } = theme.useToken();
    const { theme: currentTheme } = useUIStore();

    const contentBg = useMemo(() => {
        return currentTheme === 'dark' ? DARK_THEME_HTML_BACKGROUND_COLOR : '#FAFAFA';
    }, [currentTheme]);

    const lightThemeToken = useMemo(() => {
        return AntdTheme.getDesignToken({
            algorithm: AntdTheme.defaultAlgorithm,
            ...lightTheme,
        });
    }, []);

    const allImages = useMemo(() => getMainAndGalleryImages(script), [script]);
    const hasImages = allImages.length > 0;

    return (
        <Card loading={loading} size="small">
            <Row gutter={token.margin} style={{ marginBottom: token.margin }}>
                <Col span={8}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        {hasImages ? (
                            <>
                                <Image
                                    src={allImages[0].url}
                                    alt={script?.title || 'Script image'}
                                    style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain', cursor: 'pointer' }}
                                    onClick={onPreviewOpen}
                                    preview={false}
                                />
                                <Image.PreviewGroup
                                    items={allImages.map((img) => img.full ?? img.url).filter(Boolean)}
                                    preview={{
                                        visible: previewVisible,
                                        current: previewIndex,
                                        onVisibleChange: onPreviewVisibleChange,
                                        onChange: onPreviewChange,
                                    }}
                                />
                            </>
                        ) : (
                            <ImagePlaceholder width={100} height={100} />
                        )}
                    </div>
                </Col>
                <Col span={16}>
                    <Descriptions column={1} size="middle">
                        <Descriptions.Item label="Ціна">
                            <Typography.Text strong>
                                {script?.product_price ? script.product_price : '-'}
                            </Typography.Text>
                        </Descriptions.Item>
                        {script?.product_sku && (
                            <Descriptions.Item label="Артикул">
                                <Typography.Text strong>
                                    {script.product_sku}
                                </Typography.Text>
                            </Descriptions.Item>
                        )}
                        {script?.product_id && (
                            <Descriptions.Item label="ID продукту">
                                <Typography.Text
                                    strong
                                    style={{ width: 200 }}
                                    ellipsis={{ tooltip: script.product_id }}
                                >
                                    {script.product_id}
                                </Typography.Text>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </Col>
            </Row>
            {script?.description && (
                <ConfigProvider
                    theme={{
                        components: {
                            Collapse: {
                                headerBg: token.colorErrorBg,
                                contentBg,
                            },
                        },
                    }}
                >
                    <Collapse
                        defaultActiveKey={['description']}
                        items={[
                            {
                                key: 'description',
                                label: (
                                    <Typography.Text strong style={{ fontSize: 20, fontWeight: 'bold', color: token.colorError }}>ВАЖЛИВО ⚠️</Typography.Text>
                                ),
                                children: (
                                    <ConfigProvider
                                        theme={{
                                            algorithm: currentTheme === 'dark' ? AntdTheme.defaultAlgorithm : undefined,
                                            token: currentTheme === 'dark' ? {
                                                colorText: lightThemeToken.colorText,
                                                colorLink: lightThemeToken.colorLink,
                                            } : undefined,
                                        }}
                                    >
                                        <div
                                            dangerouslySetInnerHTML={{ __html: script.description || '' }}
                                            style={{
                                                padding: '8px 0',
                                                color: currentTheme === 'dark' ? lightThemeToken.colorText : undefined,
                                            }}
                                        />
                                    </ConfigProvider>
                                ),
                                classNames: {
                                    body: 'wswg-content',
                                },
                            },
                        ]}
                        style={{ marginBottom: token.margin }}
                    />
                </ConfigProvider>
            )}
        </Card>
    );
};
